import { Alert, AlertTitle } from '@mui/material';
import { run } from '@mdx-js/mdx';
import React from 'react';
import * as runtime from 'react/jsx-runtime';
import { BLOG_CDN_BASE, compiledBlogUrl } from './cdn';
import { BlogContext } from './context';
import { mdxComponents } from './mdxComponents';

type MDXContent = React.ComponentType<{ components?: typeof mdxComponents }>;

/**
 * Evaluate a compiled-MDX module (function-body output of `@mdx-js/mdx`
 * `compile`, fetched from the CDN) and render it with our component registry.
 *
 * Trust model: the evaluated code comes from our own S3 bucket via
 * CloudFront. The bucket must stay non-public-writable — same trust boundary
 * the old raw-markdown path already had.
 */
const useCompiledMdx = (code: string, slug: string) => {
  const [Content, setContent] = React.useState<MDXContent>();
  const [error, setError] = React.useState<Error>();

  React.useEffect(() => {
    let cancelled = false;
    setContent(undefined);
    setError(undefined);
    run(code, { ...(runtime as object), baseUrl: compiledBlogUrl(slug) } as Parameters<typeof run>[1])
      .then((mod) => {
        if (!cancelled) setContent(() => (mod as { default: MDXContent }).default);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e : new Error(String(e)));
      });
    return () => {
      cancelled = true;
    };
  }, [code, slug]);

  return { Content, error };
};

class MdxErrorBoundary extends React.Component<
  { children: React.ReactNode; onError: (e: Error) => void },
  { failed: boolean }
> {
  state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  componentDidCatch(error: Error) {
    this.props.onError(error);
  }
  render() {
    return this.state.failed ? null : this.props.children;
  }
}

const RenderError: React.FC<{ error: Error }> = ({ error }) => (
  <Alert severity="error">
    <AlertTitle>This post failed to render</AlertTitle>
    {error.message}
  </Alert>
);

const MdxRenderer: React.FC<{ code: string; slug: string; assetBase?: string }> = ({
  code,
  slug,
  assetBase = BLOG_CDN_BASE,
}) => {
  const { Content, error } = useCompiledMdx(code, slug);
  const [renderError, setRenderError] = React.useState<Error>();

  if (error || renderError) return <RenderError error={(error ?? renderError)!} />;
  if (!Content) return null; // BlogDetail shows the skeleton until code arrives

  return (
    <BlogContext.Provider value={{ slug, assetBase }}>
      <MdxErrorBoundary onError={setRenderError}>
        <Content components={mdxComponents} />
      </MdxErrorBoundary>
    </BlogContext.Provider>
  );
};

export default MdxRenderer;
